class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', event => {
      if (event.code === 'Escape') this.close();
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.dorsaCartBound === 'true') return;

    cartLink.dataset.dorsaCartBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', event => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', event => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    window.requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement && typeof trapFocus === 'function') {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    const controlledElement = cartDrawerNote.nextElementSibling;

    if (controlledElement && controlledElement.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', controlledElement.id);
    }

    cartDrawerNote.addEventListener('click', event => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    if (cartDrawerNote.parentElement && typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');

    if (inner && inner.classList.contains('is-empty')) {
      inner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach(section => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) {
        return;
      }

      const replacement = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );

      if (replacement !== null) {
        sectionElement.innerHTML = replacement;
      }
    });

    window.setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');

      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }

      this.open();
    }, 0);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);

    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}

class DorsaDeliveryDate extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  render() {
    const businessDays = Math.max(
      1,
      parseInt(this.dataset.businessDays || '4', 10)
    );

    const deliveryDate = this.addBusinessDays(new Date(), businessDays);

    this.textContent = this.formatDate(deliveryDate);

    this.setAttribute(
      'aria-label',
      `Delivery by ${deliveryDate.toLocaleDateString('en-AU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      })}`
    );
  }

  addBusinessDays(startDate, businessDays) {
    const result = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate()
    );

    let added = 0;

    while (added < businessDays) {
      result.setDate(result.getDate() + 1);

      const day = result.getDay();

      if (day !== 0 && day !== 6) {
        added += 1;
      }
    }

    return result;
  }

  ordinal(number) {
    const mod100 = number % 100;

    if (mod100 >= 11 && mod100 <= 13) return `${number}th`;

    switch (number % 10) {
      case 1:
        return `${number}st`;
      case 2:
        return `${number}nd`;
      case 3:
        return `${number}rd`;
      default:
        return `${number}th`;
    }
  }

  formatDate(date) {
    const weekday = new Intl.DateTimeFormat('en-AU', {
      weekday: 'long'
    }).format(date);

    const month = new Intl.DateTimeFormat('en-AU', {
      month: 'long'
    }).format(date);

    return `${weekday} the ${this.ordinal(date.getDate())} of ${month}`;
  }
}

if (!customElements.get('dorsa-delivery-date')) {
  customElements.define('dorsa-delivery-date', DorsaDeliveryDate);
}

class DorsaCartRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.dorsaLoaded === 'true') return;

    this.dataset.dorsaLoaded = 'true';
    this.addEventListener('submit', this.onRecommendationSubmit.bind(this));

    const results = this.querySelector('.dorsa-cart-recommendations__results');

    if (results && results.children.length > 0) {
      this.hidden = false;
      return;
    }

    this.loadRecommendations(this.dataset.url, true);
  }

  async loadRecommendations(url, allowRelatedFallback) {
    if (!url) {
      this.useFallbackCollection();
      return;
    }

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`Recommendation request failed: ${response.status}`);
      }

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      const sourceResults = parsed.querySelector(
        'dorsa-cart-recommendations .dorsa-cart-recommendations__results'
      );

      const targetResults = this.querySelector(
        '.dorsa-cart-recommendations__results'
      );

      if (sourceResults && sourceResults.children.length > 0 && targetResults) {
        targetResults.innerHTML = sourceResults.innerHTML;
        this.hidden = false;
        return;
      }

      const shouldUseRelated =
        allowRelatedFallback &&
        this.dataset.useRelatedFallback === 'true' &&
        this.dataset.relatedUrl;

      if (shouldUseRelated) {
        await this.loadRecommendations(this.dataset.relatedUrl, false);
        return;
      }

      this.useFallbackCollection();
    } catch (error) {
      console.warn('[DORSA cart] Recommendations unavailable.', error);

      const shouldUseRelated =
        allowRelatedFallback &&
        this.dataset.useRelatedFallback === 'true' &&
        this.dataset.relatedUrl;

      if (shouldUseRelated) {
        try {
          await this.loadRecommendations(this.dataset.relatedUrl, false);
          return;
        } catch (relatedError) {
          console.warn('[DORSA cart] Related recommendations unavailable.', relatedError);
        }
      }

      this.useFallbackCollection();
    }
  }

  useFallbackCollection() {
    const template = this.querySelector('template[data-dorsa-cart-fallback]');
    const target = this.querySelector('.dorsa-cart-recommendations__results');

    if (!template || !target || template.content.children.length === 0) {
      this.hidden = true;
      return;
    }

    target.innerHTML = '';
    target.appendChild(template.content.cloneNode(true));

    if (target.children.length > 0) {
      this.hidden = false;
    }
  }

  async onRecommendationSubmit(event) {
    const form = event.target.closest('.dorsa-cart-rec-form');
    if (!form || !this.contains(form)) return;

    event.preventDefault();

    const button = form.querySelector('[data-dorsa-rec-add]');
    const errorElement = form.querySelector('.dorsa-cart-rec-error');
    const cart = document.querySelector('cart-drawer');

    if (!button || !cart || button.getAttribute('aria-busy') === 'true') return;

    const selectedId = String(form.querySelector('[name="id"]')?.value || '').replace(
      'gid://shopify/ProductVariant/',
      ''
    );

    if (
      [
        '47566539686044',
        '47566539751580',
        '47566539653276',
        '47566539718812'
      ].includes(selectedId)
    ) {
      const service = window.DorsaVelarFunnel;

      if (!service?.handleVelarForm) {
        if (errorElement) {
          errorElement.textContent =
            'The VELAR purchase service could not load. Please refresh the page and try again.';
        }
        return;
      }

      await service.handleVelarForm(form, button);
      return;
    }

    if (errorElement) errorElement.textContent = '';

    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');

    try {
      const formData = new FormData(form);

      formData.append(
        'sections',
        cart.getSectionsToRender().map(section => section.id)
      );

      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(button);

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json'
        },
        body: formData
      });

      const state = await response.json();

      if (!response.ok || state.status) {
        throw new Error(
          state.description ||
          state.message ||
          'This piece could not be added.'
        );
      }

      if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined'
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'dorsa-cart-recommendations',
          productVariantId: formData.get('id'),
          cartData: state
        });
      }

      cart.classList.remove('is-empty');
      cart.renderContents(state);
    } catch (error) {
      if (errorElement) {
        errorElement.textContent =
          error && error.message
            ? error.message
            : 'This piece could not be added.';
      } else {
        console.error(error);
      }
    } finally {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('dorsa-cart-recommendations')) {
  customElements.define('dorsa-cart-recommendations', DorsaCartRecommendations);
}

document.addEventListener('shopify:section:select', event => {
  const drawer = event.target && event.target.querySelector
    ? event.target.querySelector('cart-drawer')
    : null;

  if (drawer) drawer.open();
});

document.addEventListener('shopify:section:deselect', event => {
  const drawer = event.target && event.target.querySelector
    ? event.target.querySelector('cart-drawer')
    : null;

  if (drawer) drawer.close();
});

document.addEventListener('shopify:block:select', () => {
  const drawer = document.querySelector('cart-drawer.active');
  const roomReady = drawer ? drawer.querySelector('.dorsa-room-ready') : null;

  if (roomReady) roomReady.open = true;
});